import store from 'store/store';
import { BaseRoleCode, PermissionKeyType, Permissions, RoleCode } from './permissions';
import { RoleCodes, Roles } from 'theme/constants';
import { getSelectedUserType } from 'store/selectors/selectors';

/**
 * Normalize a role by trimming and converting it to uppercase.
 * @param {string} role - The role to normalize.
 * @returns {string} - The normalized role.
 */
const normalizeRole = (role: string): string => role.trim().toUpperCase();

/**
 * Retrieves the selectedUserType from the Redux store.
 * @returns {string} - The user's role code.
 */
export const getSelectedUserTypeFromStore = (): RoleCode => {
  return getSelectedUserType(store.getState());
};

/**
 * Extracts the base role without permission suffix (EDITOR, VIEWER).
 * @param {RoleCode} roleCode - The role code to process.
 * @returns {string} - The base role code.
 */
export const getBaseRole = (roleCode: RoleCode): string => {
  return roleCode.replace(/_Editor|_Viewer/, '');
};

/**
 * Check if the user has a specific base role, ignoring permissions.
 * @param {RoleCode} baseRole - The base role to check against.
 * @returns {boolean} - True if the user's role matches the base role.
 */
export const hasBaseRole = (baseRole: BaseRoleCode): boolean => {
  const selectedUserType = getSelectedUserTypeFromStore();
  return normalizeRole(getBaseRole(selectedUserType) as RoleCode) === normalizeRole(baseRole);
};

/**
 * Check if the user has any of the specified base roles, ignoring permissions.
 * @param {BaseRoleCode[]} baseRoles - An array of base roles to check against.
 * @returns {boolean} - True if the user's role matches any of the base roles.
 */
export const hasAnyBaseRole = (baseRoles: BaseRoleCode[]): boolean => {
  const selectedUserType = getSelectedUserTypeFromStore();
  return baseRoles.some(
    (baseRole) =>
      normalizeRole(getBaseRole(selectedUserType) as RoleCode) === normalizeRole(baseRole)
  );
};

/**
 * Check if the user has a specific role.
 * @param {RoleCode} specificRole - The specific role to check against.
 * @returns {boolean} - True if the user has the specified role.
 */
export const hasRole = (specificRole: RoleCode): boolean => {
  const selectedUserType = getSelectedUserTypeFromStore();
  return normalizeRole(selectedUserType) === normalizeRole(specificRole);
};

/**
 * Check if the user has any of the specified roles.
 * @param {RoleCode[]} roles - An array of roles to check against.
 * @returns {boolean} - True if the user's role matches any role in the array.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const hasAnyRole = (roles: RoleCode[]): boolean => {
  const selectedUserType = getSelectedUserTypeFromStore();
  const normalizedUserRole = normalizeRole(selectedUserType);
  return roles.some((role) => normalizeRole(role) === normalizedUserRole);
};

/**
 * Check if the user has a role that grants the specified permission.
 * @param {PermissionKey} permissionKey - The permission to check against.
 * @returns {boolean} - True if the user has the required permission.
 */
export const hasPermission = (permissionKey: PermissionKeyType): boolean => {
  const allowedRoles = Permissions[permissionKey].map((role) => normalizeRole(role)) || [];
  const selectedUserType = getSelectedUserTypeFromStore();
  return allowedRoles.includes(normalizeRole(selectedUserType));
};

/**
 * Convert a role code to a role label.
 * @param {string} roleCode - The roleCode to convert into a role.
 * @returns {string} - The corresponding role label or 'Unknown Role' if not found.
 */
export const userRoleCodeToRole = (roleCode: RoleCode): string => {
  const labels: { [key: string]: string } = {
    [RoleCodes.SHIP_AGENT_EDITOR_CODE]: Roles.SHIP_AGENT_EDITOR,
    [RoleCodes.SHIP_AGENT_VIEWER_CODE]: Roles.SHIP_AGENT_VIEWER,
    [RoleCodes.TRANSPORT_OPERATOR_EDITOR_CODE]: Roles.TRANSPORT_OPERATOR_EDITOR,
    [RoleCodes.TRANSPORT_OPERATOR_VIEWER_CODE]: Roles.TRANSPORT_OPERATOR_VIEWER,
    [RoleCodes.RELEASE_PARTY_EDITOR_CODE]: Roles.RELEASE_PARTY_EDITOR,
    [RoleCodes.RELEASE_PARTY_VIEWER_CODE]: Roles.RELEASE_PARTY_VIEWER,
    [RoleCodes.TERMINAL_EDITOR_CODE]: Roles.TERMINAL_EDITOR,
    [RoleCodes.TERMINAL_VIEWER_CODE]: Roles.TERMINAL_VIEWER,
  };
  return labels[roleCode] || 'Unknown Role';
};

/**
 * Convert a role code to a base role label.
 * @param {string} roleCode - The roleCode to convert into a role.
 * @returns {string} - The corresponding role label or 'Unknown Role' if not found.
 */
export const userRoleCodeToBaseRole = (roleCode: RoleCode): string => {
  const labels: { [key: string]: string } = {
    [RoleCodes.SHIP_AGENT_EDITOR_CODE]: Roles.SHIP_AGENT,
    [RoleCodes.SHIP_AGENT_VIEWER_CODE]: Roles.SHIP_AGENT,
    [RoleCodes.TRANSPORT_OPERATOR_EDITOR_CODE]: Roles.TRANSPORT_OPERATOR,
    [RoleCodes.TRANSPORT_OPERATOR_VIEWER_CODE]: Roles.TRANSPORT_OPERATOR,
    [RoleCodes.RELEASE_PARTY_EDITOR_CODE]: Roles.RELEASE_PARTY,
    [RoleCodes.RELEASE_PARTY_VIEWER_CODE]: Roles.RELEASE_PARTY,
    [RoleCodes.TERMINAL_EDITOR_CODE]: Roles.TERMINAL,
    [RoleCodes.TERMINAL_VIEWER_CODE]: Roles.TERMINAL,
  };
  return labels[roleCode] || 'Unknown Role';
};

export const getValidRoles = (roleCodes: string[]): RoleCode[] => {
  const validRoleCodes = Object.values(RoleCodes);
  return roleCodes?.filter((code) => validRoleCodes.includes(code)) as RoleCode[];
};
